"use client" // NextJS 13 requires this. Remove if you are using NextJS 12 or lower

import { useEffect } from "react"
import Script from "next/script"
import { useSelector } from "@legendapp/state/react"
import { appStore$ } from "@/stores/app.v2.store"
import { useIntercom } from "@/hooks/useIntercom"
import { syncStore$ } from "@/stores/sync.v2.store"
import { featurebaseStore$ } from "@/stores/featurebase.store"

interface IFeatureBaseProps {
  userHash?: string
  companyHash?: string
}

export function FeatureBase({ companyHash, userHash }: IFeatureBaseProps) {
  const { isReady } = useIntercom()
  const userData = useSelector(() => appStore$.userData.get() ?? undefined)

  useEffect(() => {
    const win = window as any
    if (typeof win.Featurebase !== "function") {
      win.Featurebase = function () {
        ;(win.Featurebase.q = win.Featurebase.q || []).push(arguments)
      }
    }
    if (!isReady) {
      return
    }

    if (!userHash) {
      console.error("Featurebase: User hash is required")
      return
    }

    if (!companyHash) {
      console.error("Featurebase: Company hash is required")
      return
    }

    win.Featurebase(
      "identify",
      {
        // Each 'identify' call should include an "organization"
        // property, which is your Featurebase board's name before the
        // ".featurebase.app".
        organization: process.env.NEXT_PUBLIC_FEATUREBASE_ORG,
        userHash,
        // Required fields. Replace with your customers data.
        // Both email and userId should be provided when possible
        // At minimum, either email or userId must be present
        email: userData.user.email,
        name: userData.user.fullName,
        userId: userData.user.id.toString(),
        // Optional - include other fields as needed
        customFields: {
          created_at: new Date(userData.user.dateCreated).toISOString(),
          supervisor: `${userData.user.isAdmin}`,
          account_owner: `${userData.user?.isOwner}`,
          user_call_minutes_last30days: `${userData.user.callMinutesLast30Days}`,
          user_dialer_sessions_last30days: `${userData.user.dialingSessionsLast30Days}`,
          vmdrop_setup: `${userData.user.vmdropSetup}`,
          //   title: "Product Manager",
          //   plan: "Premium",
          //   number: "123",
        },
        // Optional, uncomment if you are looking to use multilingual changelog
        locale: "en", // Will make sure the user receives the changelog email in the correct language
        // Optional - add user company information as needed
        companies: [
          {
            id: userData.user.accountId.toString(), // required
            name: userData.user.companyName, // required
            companyHash,
            createdAt: new Date(userData.user.dateCreated).toISOString(), // optional
            customFields: {
              account_state: userData.user.accountState,
              channel: userData.user.accountSignupChannel,
              user_count: `${userData.user.accountSeats}`,
              crm_user_count: `${userData.user.crmSeats}`,
              account_call_minutes_last30days: `${userData.user.accountCallMinutesLast30Days}`,
              account_dialer_sessions_last30days: `${userData.user.accountDialingSessionsLast30Days}`,
              sms_enabled: `${userData.user.accountSmsEnabled}`,
              account_trial_type: userData.user.accountTrialType,
              account_activation_status: userData.user.accountTrialActivated ? "Activated" : "Limited",
              account_email_marketing_opt_in: `${userData.user.emailMarketingOptIn}`,
              account_sms_opt_in: `${userData.user.smsOptIn}`,
              account_activation_hash: `${userData.user.rg_acct_hash}`,
              //   industry: "Fintech",
              //   location: "Canada",
            }, // optional
          },
        ], // optional
      },
      (err: unknown) => {
        // Callback function. Called when identify completed.
        if (err) {
          console.error(err)
        } else {
          console.log("Data sent successfully!")
          console.log("Initializing portal widget...")
          win.Featurebase(
            "boot",
            {
              appId: "676445c81c7b5b89d586d90e", // TODO: Add this to the environment variables
              email: userData.user.email,
              userId: userData.user.id.toString(),
              createdAt: new Date(userData.user.dateCreated).toISOString(),
              theme: "light",
              locale: "en",
              hideDefaultLauncher: true,
              alignment: "left",
              userHash,
              organization: process.env.NEXT_PUBLIC_FEATUREBASE_ORG, // Replace this with your organization name, copy-paste the subdomain part from your Featurebase workspace url (e.g. https://*yourorg*.featurebase.app)
              app_version: process.env.NEXT_PUBLIC_VERSION,
              build_time: process.env.NEXT_PUBLIC_BUILD_TIME,
              cluster: syncStore$.cluster.remoteUrlS.get(),
              user_agent: window.navigator.userAgent,
              screen_resolution: `${window.screen.width}x${window.screen.height}`,
            },
            (err: unknown) => {
              if (err) {
                console.error(err)
              }

              console.log("Setting up listener when hiding Featurebase")
              win.Featurebase("onHide", function () {
                featurebaseStore$.isOpen.set(false)
                console.log("Messenger was hidden")
                // Do something when messenger is hidden
              })
              console.log("Completed")

              console.log("Setting up listener when showing Featurebase")
              win.Featurebase("onShow", function () {
                featurebaseStore$.isOpen.set(true)
                console.log("Messenger was shown")
              })
              console.log("Completed")
            }
          )
          console.log("Portal widget initialized!")

          console.log("Initializing changelog widget...")
          win.Featurebase(
            "init_changelog_widget",
            {
              organization: process.env.NEXT_PUBLIC_FEATUREBASE_ORG, // Replace this with your organization name, copy-paste the subdomain part from your Featurebase workspace url (e.g. https://*yourorg*.featurebase.app)
              dropdown: {
                enabled: false, // Add this to enable the dropdown view of the changelog
                placement: "right", // Add this to change the placement of the dropdown
              },
              popup: {
                enabled: true, // Add this to enable the popup view of the changelog
                usersName: userData.user.fullName, // This will show the user's name in the popup as a greeting
                autoOpenForNewUpdates: true, // This will open the popup for new updates
              },
              // category: [], // Filter results by changelog category, add category names in array, the names are case-sensitive
              theme: "light", // Choose between dark or light theme
              locale: "en", // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
            },
            (error: unknown, data: any) => {
              if (error) {
                console.error(error)
              }

              if (data?.action === "unreadChangelogsCountChanged") {
                console.debug("unreadChangelogsCountChanged", data.unreadCount)
                featurebaseStore$.unreadCount.set(typeof data.unreadCount === "number" ? data.unreadCount : Number.parseInt(data.unreadCount))
              }
            }
          )
          console.log("Changelog widget initialized!")
        }
      }
    )
  }, [
    companyHash,
    isReady,
    userData.user.accountCallMinutesLast30Days,
    userData.user.accountDialingSessionsLast30Days,
    userData.user.accountId,
    userData.user.accountSeats,
    userData.user.accountSignupChannel,
    userData.user.accountSmsEnabled,
    userData.user.accountState,
    userData.user.accountTrialActivated,
    userData.user.accountTrialType,
    userData.user.callMinutesLast30Days,
    userData.user.companyName,
    userData.user.crmSeats,
    userData.user.dateCreated,
    userData.user.dialingSessionsLast30Days,
    userData.user.email,
    userData.user.emailMarketingOptIn,
    userData.user.fullName,
    userData.user.id,
    userData.user.isAdmin,
    userData.user?.isOwner,
    userData.user.rg_acct_hash,
    userData.user.smsOptIn,
    userData.user.vmdropSetup,
    userHash,
  ])

  return (
    <>
      <Script src="https://do.featurebase.app/js/sdk.js" id="featurebase-sdk" strategy="afterInteractive" />
    </>
  )
}
